import React, { useMemo, useState } from 'react'
import { ICountryAverage, IStableAverage } from '../../shared/types/Stabledata.type'
import { TinsightParam } from '../../shared/types/computedKeyParam.type'
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import { t } from 'i18next'
import palette from '../../theme/palette'
import { flatMap, groupBy, isEmpty, toNumber } from 'lodash'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from "highcharts";

interface ICountryAvgPage {
    data: ICountryAverage,
    insightParams: TinsightParam[],
    username: string,
}

const CountryAvgPage: React.FC<ICountryAvgPage> = ({ data, insightParams, username }) => {
    const [customRowIndexes, setCustomRowIndexes] = useState<number[]>([]);
    const [customColorIndexes, setCustomColorsIndexes] = useState<{ row: number, col: number, color: string }[]>([]);





    const columns = [
        t('equi:registration:address:country'),
        ...data.total.map(avg => {
            let param = insightParams.find(ip => ip.name === avg.parameter_name)
            if (!param) return ""
            return [param.name_translated, `(${param.unit_translated})`].join(' ')
        }
        )
    ]

    const rows = useMemo(() => {
        if (isEmpty(data)) return []
        const country_avgs = data.country_averages.map(avg => {

            return [avg.country, ...avg.averages.map(v => v.formatted_value)]
        })
        return [...country_avgs, [
            t('avgGeneral'),
            ...data.total.map(v => v.formatted_value)]]
    }, [data])



    const getColor = (row: number, cell: number) => {
        if (isEmpty(customColorIndexes)) return 'inherit'

        let findColor = customColorIndexes.find(custom => custom.row === row && custom.col === cell)
        return findColor ? findColor.color : 'inherit'

    }

    const countries = data?.chart_country_averages?.map(avg => avg.country) || []

    const getCountriesData = () => {
        //Get Keys
        let countries = data?.chart_country_averages || []
        if (isEmpty(countries)) return []

        let element = countries.at(0)?.averages || []
        if (isEmpty(element)) return []

        let keys = element.map(e => ({ param: e.parameter_name, color: e.color }))


        return keys.map(key => {
            let values = countries.map(country => country.averages.find(v => v.parameter_name === key.param)?.raw_numeric_value.toFixed(1))
            return {
                name: insightParams.find(ip => ip.name === key.param)?.name_translated || key.param,
                data: values.map(value => toNumber(value)),
                color: key.color
            }

        })

    }

    const getUserData = () => {

        let element = data?.user_chart_average || []
        if (isEmpty(element)) return []

        let keys = element.map(e => ({ param: e.parameter_name, color: e.color }))


        return keys.map(key => {
            let value = element.find(el => el.parameter_name === key?.param)?.raw_numeric_value?.toFixed(1)
            return {
                name: insightParams.find(ip => ip.name === key.param)?.name_translated || key.param,
                data: [Number(value)],
                color: key.color
            }

        })

    }

    const chartOptions: Highcharts.Options = {
        chart: {
            type: "column",
            backgroundColor: "transparent",
            height: "250px"
        },
        title: { text: "" },
        xAxis: {
            categories: countries,
        },
        yAxis: {
            min: 0,
            title: { text: "" },
            gridLineDashStyle: "Dash",
            gridLineWidth: 0,
        },
        tooltip: { enabled: false }, // disable tooltip
        legend: {
            align: "center",
            verticalAlign: "top",
        },
        plotOptions: {
            series: {
                states: {
                    hover: { enabled: false },
                    inactive: { opacity: 1 },
                },
            },
            column: {
                grouping: true,
                borderWidth: 0,
                borderRadius: 0,
                pointPadding: 0.1,
                groupPadding: 0.2,
                dataLabels: {
                    enabled: true,
                    inside: false,
                    style: {
                        textOutline: "none",
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "11px",
                    },
                    formatter(this: Highcharts.PointLabelObject) {
                        return this.y?.toString() ?? "";
                    },
                },
            },
        },
        series: getCountriesData() as any,
        credits: { enabled: false },
    };

    const userChartOptions: Highcharts.Options = {
        chart: {
            type: "column",
            backgroundColor: "transparent",
            height: "228px",
            spacingTop: 50,
        },
        title: { text: "" },
        xAxis: {
            categories: [username],
        },

        yAxis: {
            min: 0,
            title: { text: "" },
            gridLineDashStyle: "Dash",
            gridLineWidth: 0,
        },
        tooltip: { enabled: false }, // disable tooltip
        legend: {
            enabled: false,
        },
        plotOptions: {
            series: {
                states: {
                    hover: { enabled: false },
                    inactive: { opacity: 1 },
                },
            },
            column: {
                borderRadius: 0,
                grouping: true,
                borderWidth: 0,
                pointPadding: 0.1,
                groupPadding: 0.2,
                dataLabels: {
                    enabled: true,
                    inside: false,
                    style: {
                        textOutline: "none",
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "11px",
                    },
                    formatter(this: Highcharts.PointLabelObject) {
                        return this.y?.toString() ?? "";
                    },
                },
            },
        },
        series: getUserData() as any,
        credits: { enabled: false },
    };


    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t(`stable:title:${data.type}`)}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 1, tableLayout: 'auto' }}>
                <Table
                    sx={{
                        width: '100%',
                        '& td, & th': {
                            fontSize: '12px',
                        },
                        '& th': {
                            lineHeight: '1.1',
                        }
                    }}
                    size="small" aria-label="dense table" >
                    <TableHead
                        sx={{
                            '& td, & th': {
                                borderBottom: 'none',
                                boxSizing: 'border-box',
                            },
                        }}
                    >
                        <TableRow
                            sx={{
                                background: `linear-gradient(to right, ${palette.light.primary.darker}, ${palette.light.primary.main})`,
                            }}
                        >
                            {columns.map((col, index) => (
                                <TableCell key={col}
                                    sx={{
                                        py: 0.25,
                                        px: 0.5,
                                        fontWeight: 600,
                                        color: 'white',
                                        textAlign: "center"
                                    }}
                                >
                                    {col}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody
                        sx={{
                            width: '100%',
                            '& td, & th': {
                                padding: '3px !important',
                                boxSizing: 'border-box',

                            },

                        }}
                    >
                        {rows.map((row, rowIndex) => (
                            <TableRow
                                className='listItemExam'
                                style={{ backgroundColor: rowIndex === rows.length - 1 ? palette.light.primary.light : customRowIndexes.includes(rowIndex) ? palette.light.primary.lighter : undefined }}
                                key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <TableCell
                                        sx={{
                                            backgroundColor: getColor(rowIndex, cellIndex),
                                            textAlign: 'center',
                                            fontWeight: cellIndex === 0 || rowIndex === rows.length - 1 ? 600 : 'initial',
                                            // whiteSpace: cellIndex === 0 ? 'nowrap' : 'normal', // first column no break
                                            // overflow: cellIndex === 0 ? 'hidden' : 'visible',
                                            // textOverflow: cellIndex === 0 ? 'ellipsis' : 'unset',
                                            // textAlign: cellIndex === 0 ? 'left' : 'right',
                                            breakInside: 'avoid',
                                            fontSize: 12,
                                        }}
                                        key={cellIndex}>{cell}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box width={'1200px'} display={'flex'} minHeight={'100%'} mt={3} gap={5} >
                <Box width={'700px'}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                </Box>
                <Box width={'500px'}>
                    <Typography>{t('stable:lastMonthAvg')}</Typography>
                    <HighchartsReact highcharts={Highcharts} options={userChartOptions} />
                </Box>
            </Box>

        </>
    )
}

export default CountryAvgPage
